/**
 * usePreviewSession — manages preview session lifecycle for HTML preview iframes.
 *
 * Phase 2 flow (see docs/plans/2026-05-21_PREVIEW_SANDBOX.md):
 *
 *   1. On mount/input change, POST /api/preview/session WITHOUT rootId so
 *      the server runs its root-resolver (rule 1/2/3) and consent check.
 *   2. Server returns 200 with a discriminated body — either
 *      `{ sessionId, rootId, ... }` (session created → render the iframe)
 *      or `{ needsConsent: true, proposedRoot, ... }` (caller renders the
 *      consent banner). We use 200-with-shape rather than 4xx so the
 *      needsConsent step doesn't log as a red error in DevTools — it's
 *      an expected flow step, not a failure.
 *   3. Caller calls `approveConsent()` → POST /api/preview/consent + retry
 *      session creation. The peek-session path (Single File Mode + the
 *      pre-consent blurred preview behind the banner) is owned by
 *      PreviewConsentBanner itself.
 *
 * The hook stays in charge of session lifecycle (creation, periodic
 * refresh, cleanup on unmount). Callers own the consent UX.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { request, getDefaultHeaders, getApiBase, sessionFetch, currentSessionSignal, sessionEndedSince, errorText } from '@papan/api-client'
import { onSessionRotated } from '@papan/auth/session-rotation'

/**
 * Where the preview iframe loads its content from.
 *
 * Normal envs (golink/stagetest production, local dev) bake the
 * preview origin in at build time via VITE_PREVIEW_ORIGIN — a
 * dedicated subdomain on a different origin from both the frontend
 * AND the API, so:
 *   - the sandboxed iframe is cross-origin to the frontend
 *     (defense-in-depth on top of `sandbox=""`)
 *   - auth cookies set on the API host don't apply to the preview
 *     host, so iframe sub-requests can't piggyback on user auth
 *
 * Per-PR review envs share a single CF Pages build with stagetest,
 * so VITE_PREVIEW_ORIGIN is baked as `preview-stagetest.papan.ai` —
 * the wrong backend (sessions live in pr-<slug>-api, which
 * preview-stagetest can't see → 404). Override at runtime to point
 * at the per-PR dedicated preview subdomain. That host is provisioned
 * by `scripts/deploy-pr.sh` — same backend as the per-PR API, but a
 * different nginx vhost that blocks /api/* at the nginx layer and
 * isn't covered by the API's cookie scope. Mirrors the prod model.
 */
// Origin resolution + PR-hostname handling extracted to previewOrigin.ts
// (#235) so the share-view /s/ URLs share the exact same code path.
// PR_PREVIEW_HOSTNAME is re-exported for existing importers.
import { getPreviewOrigin } from './previewOrigin.js'
export { PR_PREVIEW_HOSTNAME } from './previewOrigin.js'
const PREVIEW_ORIGIN = getPreviewOrigin()
const REFRESH_INTERVAL = 20 * 60 * 1000 // 20 minutes

export type PreviewRootSource = 'miniapp' | 'config' | 'static' | 'explicit'

export interface PreviewRootInfo {
  rootId: string
  rootPath: string
  source: PreviewRootSource
  /** Entry-file path relative to the root, in the root's namespace. Set by
   *  the server only for `.shared` previews, where the recipient's own file
   *  path is in a different tenant namespace than the (source-tenant) root so
   *  the client can't derive it. Undefined for same-tenant previews — the
   *  client computes it from its file path and rootPath. */
  entryPath?: string
}

export interface PendingConsent {
  /** RootId the server proposes. */
  rootId: string
  /** Human-readable path of the proposed root (for the banner). */
  rootPath: string
  /** Which rule produced the proposal. `miniapp` never appears here
   *  (mini-app rooted previews skip the consent gate). */
  source: 'config' | 'static'
  /** Entry-file path relative to the proposed root (see PreviewRootInfo). Used
   *  by the no-script peek behind the consent banner. `.shared` only. */
  entryPath?: string
  /** Recipient-addressable id for browsing the proposed root's children (the
   *  banner's folder-tree popover). `.shared` only — the proposed `rootId` is a
   *  source-tenant id that lists empty in the recipient tenant. */
  browseRootId?: string
}

interface UsePreviewSessionOptions {
  fileId: string
  mode: 'preview' | 'view'
  /** Set false to disable the hook (no session created). */
  enabled: boolean
  /** HTML content override for the entry file (e.g., version preview). */
  contentOverride?: string | null
  /**
   * Skip the mint flow and use this session id directly. Used by the
   * share-view path: the share-session token IS a valid preview-session
   * token (kind='share') and the recipient lacks Drive auth to call
   * `POST /api/preview/session` anyway. When provided, no consent flow,
   * no refresh interval, no delete-on-unmount — the share session's
   * lifecycle is owned by whoever minted it.
   */
  sessionIdOverride?: string | null
  /**
   * #1408: run the app on its OWN preview origin when the server offers one
   * (`appPreviewOrigin` on the mint) — a real origin, where camera, microphone and screen
   * work. Without one (a host with no APP_PREVIEW_DOMAIN) the session stays on the shared
   * opaque preview origin, exactly as before. Opt-in per surface; pass `appOrigin` from the
   * result to HtmlViewer so its frames and messaging match.
   */
  appOrigin?: boolean
}

interface UsePreviewSessionResult {
  sessionId: string | null
  /** Preview origin URL (e.g., https://preview-golink.papan.ai). */
  previewOrigin: string
  /** Full base URL for the iframe: {origin}/s/{sessionId}. Null while
   *  loading, while pending consent, or when disabled. */
  baseUrl: string | null
  /** #1408: the app's own preview origin `baseUrl` is on, or null (the shared preview origin). */
  appOrigin: string | null
  /** Info about the rootId the session was created with. Null until a
   *  session exists. */
  currentRoot: PreviewRootInfo | null
  /** Set when the server returned a 200 needsConsent body (discriminated
   *  on shape, not status — see the file-level JSDoc). Caller renders banner. */
  pendingConsent: PendingConsent | null
  /** Approve the pending consent and retry session creation. */
  approveConsent: () => Promise<void>
  /** Force the session to re-create — used after the toolbar picker
   *  changes the org-wide root config so the iframe reflects the new scope. */
  recreate: () => Promise<void>
  /** Error message if session creation failed for an unexpected reason. */
  error: string | null
}

interface SessionSuccessResponse {
  sessionId: string
  expiresIn: number
  rootId: string
  rootPath?: string
  source: PreviewRootSource
  entryPath?: string
  /** #1408: where this session's app runs on its own origin; absent when the host has none. */
  appPreviewOrigin?: string
}

interface NeedsConsentResponse {
  needsConsent: true
  proposedRoot: { id: string; path: string }
  source: 'config' | 'static'
  entryPath?: string
  browseRootId?: string
}

export function usePreviewSession({
  fileId,
  mode,
  enabled,
  contentOverride,
  sessionIdOverride,
  appOrigin: wantAppOrigin = false,
}: UsePreviewSessionOptions): UsePreviewSessionResult {
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [appPreviewOrigin, setAppPreviewOrigin] = useState<string | null>(null)
  const [currentRoot, setCurrentRoot] = useState<PreviewRootInfo | null>(null)
  const [pendingConsent, setPendingConsent] = useState<PendingConsent | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sessionIdRef = useRef<string | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Stable references so `create`'s identity doesn't churn on input changes
  // we don't care about — only refire the effect on the inputs that actually
  // change the session shape.
  const fileIdRef = useRef(fileId)
  const modeRef = useRef(mode)
  const contentOverrideRef = useRef(contentOverride)
  fileIdRef.current = fileId
  modeRef.current = mode
  contentOverrideRef.current = contentOverride

  /** `create`, for callbacks defined before it (the refresh interval). */
  const createRef = useRef<() => Promise<void>>(async () => {})

  const stopRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [])

  const startRefresh = useCallback(() => {
    stopRefresh()
    refreshTimerRef.current = setInterval(() => {
      const sid = sessionIdRef.current
      if (!sid) return
      const start = currentSessionSignal()
      request(`/api/preview/session/${sid}/refresh`, { method: 'POST' }).catch((err: { status?: number }) => {
        // #1316: its session ended meanwhile — a re-mint now would mint as the NEXT session
        if (sessionEndedSince(start, err)) return
        // Gone — e.g. revoked with the account's sessions after a password change
        // (#601): re-mint from the live session instead of keeping an iframe every
        // request of which is refused. A signed-out browser answers 401 here, not
        // 404, and re-mints nothing.
        if (err?.status === 404 && sessionIdRef.current === sid) void createRef.current()
      })
    }, REFRESH_INTERVAL)
  }, [stopRefresh])

  /**
   * Core create. Always lets the server resolve rootId (rule 1/2/3 + consent).
   * Used by:
   *   - effect on mount (first session attempt)
   *   - approveConsent (retry after consent row written → silent success)
   *   - recreate (toolbar [Change…] changed the org-wide config)
   */
  const create = useCallback(
    async () => {
      abortRef.current?.abort()
      const abort = new AbortController()
      abortRef.current = abort

      // Capture the previous session so we can DELETE it server-side once
      // the new one is established. Without this, `recreate()` (toolbar
      // [Change…]) and `approveConsent()` overwrite sessionIdRef and leave
      // the prior session's contentOverride lingering in Redis until its
      // 24h TTL. Flipping the root picker N times would otherwise leave
      // N-1 orphan Redis entries per tab.
      const prevSessionId = sessionIdRef.current

      try {
        const body: Record<string, unknown> = {
          fileId: fileIdRef.current,
          mode: modeRef.current,
        }
        const co = contentOverrideRef.current
        if (co) body.contentOverride = co

        const response = await sessionFetch(`${getApiBase()}/api/preview/session`, {
          method: 'POST',
          credentials: 'include',
          // Include X-Tenant-Id / X-Org-Email via getDefaultHeaders so
          // the server's resolveTenant middleware sets auth.tenantId +
          // auth.orgEmail correctly (otherwise it falls back to the
          // user's personal tenant + primary email, which breaks the
          // permission lookup for files in org tenants where the user's
          // membership email differs from their primary email).
          headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
          body: JSON.stringify(body),
          signal: abort.signal,
        })
        if (abort.signal.aborted) return

        if (!response.ok) {
          const text = await response.text()
          throw new Error(`Session creation failed: ${response.status} ${text}`)
        }

        // The 200 body is a discriminated union: either needsConsent (the
        // server wants the user to approve a proposed root) or a created
        // session. We discriminate on body shape, not status, so the
        // expected "consent required" flow doesn't show as a red 4xx in
        // DevTools.
        const data = (await response.json()) as
          | NeedsConsentResponse
          | SessionSuccessResponse

        // Release the prior session server-side regardless of which terminal
        // state we land in (needsConsent / success / error). Once we wipe
        // sessionIdRef.current below, the prev session is orphaned on the
        // client; the in-memory session map would hold it until 24h TTL or
        // MAX_SESSIONS eviction. Cheap to always DELETE — fire-and-forget.
        const releasePrev = () => {
          if (prevSessionId && prevSessionId !== sessionIdRef.current) {
            request(`/api/preview/session/${prevSessionId}`, { method: 'DELETE' }).catch(() => {})
          }
        }

        if ('needsConsent' in data) {
          setPendingConsent({
            rootId: data.proposedRoot.id,
            rootPath: data.proposedRoot.path,
            source: data.source,
            entryPath: data.entryPath,
            browseRootId: data.browseRootId,
          })
          setSessionId(null)
          sessionIdRef.current = null
          setCurrentRoot(null)
          setError(null)
          stopRefresh()
          releasePrev()
          return
        }

        setAppPreviewOrigin(typeof data.appPreviewOrigin === 'string' ? data.appPreviewOrigin : null)
        setSessionId(data.sessionId)
        sessionIdRef.current = data.sessionId
        setCurrentRoot({
          rootId: data.rootId,
          rootPath: data.rootPath ?? '',
          source: data.source,
          entryPath: data.entryPath,
        })
        setPendingConsent(null)
        setError(null)
        startRefresh()
        releasePrev()
      } catch (err: any) {
        // #1316: an answer dropped because the session ended is not a failure to show
        const text = errorText(err, 'Failed to create preview session')
        if (abort.signal.aborted || text === null) return
        setError(text)
        setSessionId(null)
        sessionIdRef.current = null
        setCurrentRoot(null)
        setPendingConsent(null)
        stopRefresh()
        // Release prev on error too — sessionIdRef is now null, so the prev
        // session is orphaned client-side; release server-side to match.
        if (prevSessionId) {
          request(`/api/preview/session/${prevSessionId}`, { method: 'DELETE' }).catch(() => {})
        }
      }
    },
    [startRefresh, stopRefresh],
  )
  createRef.current = create

  const approveConsent = useCallback(async () => {
    if (!pendingConsent) return
    try {
      await request('/api/preview/consent', {
        method: 'POST',
        body: JSON.stringify({
          fileId: fileIdRef.current,
          rootId: pendingConsent.rootId,
        }),
        headers: { 'Content-Type': 'application/json' },
      })
      // Consent row now matches — retry without explicit rootId.
      await create()
    } catch (err: any) {
      const text = errorText(err, 'Failed to record consent')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
    }
  }, [pendingConsent, create])

  // Create the session on enable / fileId / mode / contentOverride change.
  // The server picks the rootId via its resolver (rule 1/2/3), so the
  // hook doesn't take a parentId prop anymore — the previous "parentId
  // as fallback" comment referred to a removed pre-Phase-2 prop.
  //
  // Share-view path: when sessionIdOverride is set, skip the mint flow
  // entirely. The share session was minted upstream and its lifecycle
  // (revoke, expiry, max-views) is owned by the share-link row, not by
  // this component. No POST, no DELETE, no refresh interval.
  useEffect(() => {
    if (sessionIdOverride) {
      setSessionId(sessionIdOverride)
      sessionIdRef.current = sessionIdOverride
      setCurrentRoot(null)
      setPendingConsent(null)
      setError(null)
      stopRefresh()
      return
    }

    if (!enabled || !fileId || !PREVIEW_ORIGIN) {
      setSessionId(null)
      sessionIdRef.current = null
      setCurrentRoot(null)
      setPendingConsent(null)
      setError(null)
      stopRefresh()
      return
    }

    create()

    return () => {
      abortRef.current?.abort()
      stopRefresh()
      const id = sessionIdRef.current
      if (id) {
        sessionIdRef.current = null
        request(`/api/preview/session/${id}`, { method: 'DELETE' }).catch(() => {})
      }
    }
  }, [enabled, fileId, mode, contentOverride, create, stopRefresh, sessionIdOverride])

  // A password change on this browser revoked the session this preview was
  // minted from (#601): re-mint now, not at the next 20-minute refresh. A
  // share-view override is not ours to re-mint.
  useEffect(() => {
    if (sessionIdOverride) return
    return onSessionRotated(() => {
      if (sessionIdRef.current) void createRef.current()
    })
  }, [sessionIdOverride])

  // #1408: only a session THIS hook minted carries an app origin (a share-view override's lifecycle
  // and origin are its owner's).
  const appOrigin = wantAppOrigin && !sessionIdOverride && sessionId ? safeHttpsOrigin(appPreviewOrigin) : null
  const baseUrl = sessionId && PREVIEW_ORIGIN
    ? `${appOrigin ?? PREVIEW_ORIGIN}/s/${sessionId}`
    : null

  const recreate = useCallback(async () => {
    await create()
  }, [create])

  return {
    sessionId,
    previewOrigin: PREVIEW_ORIGIN,
    baseUrl,
    appOrigin,
    currentRoot,
    pendingConsent,
    approveConsent,
    recreate,
    error,
  }
}

/** An https origin exactly as given, or null — what the server returned is used only in that shape. */
function safeHttpsOrigin(v: string | null): string | null {
  if (!v) return null
  try {
    const u = new URL(v)
    return u.protocol === 'https:' && u.origin === v ? v : null
  } catch {
    return null
  }
}
